import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { formatAnnexLabel, type AdminRuleListItem } from '@safetyhub/shared';
import { adminApi, extractApiError } from '../../lib/api';

/** 조회 키 — `['admin','regulations']` 접두의 하위. 고시는 수동 수집(`POST /admin/admin-rules/sync`)에만 바뀐다 */
export const ADMIN_RULES_QUERY_KEY = ['admin', 'regulations', 'admin-rules'] as const;
export const ADMIN_RULE_QUERY_KEY = (id: string) => ['admin', 'regulations', 'admin-rules', id] as const;

interface AdminRulesModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * **고시(법제처 행정규칙) 열람** — 출시 조건 ②-f · 설계 `docs/plans/2026-11-02-admin-rule-collection-design.md` §5.
 *
 * 안전검사 대상 기계의 규격 임계값처럼 **법정 의무 판정 규칙이 고시에 있는 것**을 운영자가 원문으로 읽는 자리다. 읽기 전용이다 —
 * 수집은 법제처 외부 호출이라 이 화면에 버튼이 없고(사용자 확정 A3 ⓑ), 판정 규칙은 법정 의무 분류 표에 사람이 옮긴다(②-c).
 *
 * 목록은 **버전 단위**다(한 고시의 옛 버전도 각 행). 「현행」은 서버 재평가(`reevaluateAdminRuleCurrency`)가 정한다.
 * 문장을 가른다: 「수집된 고시 없음」(아직 수집 전) · 「조문 없음」 · 「별표 없음」.
 * 날짜(`issueDate`·`enforcementDate`)는 시각이 없는 `YYYY-MM-DD`라 시간대 변환 없이 그대로 쓴다.
 */
export default function AdminRulesModal({ open, onClose }: AdminRulesModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelectedId(null);
  }, [open]);

  const listQuery = useQuery({
    queryKey: ADMIN_RULES_QUERY_KEY,
    queryFn: async () => {
      const res = await adminApi.getAdminRules();
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data;
    },
    enabled: open,
  });

  const items = listQuery.data?.items ?? [];
  const selected: AdminRuleListItem | null = useMemo(() => {
    if (items.length === 0) return null;
    return items.find((i) => i.id === selectedId) ?? items.find((i) => i.isCurrent) ?? items[0];
  }, [items, selectedId]);

  const detailQuery = useQuery({
    queryKey: ADMIN_RULE_QUERY_KEY(selected?.id ?? ''),
    queryFn: async () => {
      const res = await adminApi.getAdminRule(selected!.id);
      if (!res.data) throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
      return res.data;
    },
    enabled: open && !!selected,
  });

  if (!open) return null;
  const detail = detailQuery.data;

  return (
    <Modal
      open
      onClose={onClose}
      title="고시(행정규칙)"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          법제처에서 받은 고시 원문입니다. 판정 기준(규격 임계값 등)은 법정 의무 카탈로그에 옮겨 입력하고, 이 원문은 그 대조용으로 읽습니다.
        </p>

        {listQuery.isLoading ? (
          <div className="py-6 text-sm text-gray-500">불러오는 중...</div>
        ) : listQuery.error ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
            고시 목록을 불러오지 못했습니다. {extractApiError(listQuery.error).message}
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-sm text-gray-500">수집된 고시가 없습니다. 고시 수집이 아직 실행되지 않았습니다.</div>
        ) : (
          <div className="grid grid-cols-[18rem_1fr] gap-4 min-h-[24rem]">
            <ul aria-label="고시 목록" className="divide-y divide-gray-100 border rounded-card overflow-y-auto max-h-[60vh]">
              {items.map((i) => {
                const isSelected = selected?.id === i.id;
                return (
                  <li key={i.id}>
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setSelectedId(i.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        isSelected ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-800'
                      }`}
                    >
                      <span className="block">
                        {i.name}
                        {i.isCurrent && (
                          <span className="ml-2 px-1.5 py-0.5 text-[11px] rounded bg-green-100 text-green-700">현행</span>
                        )}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {/* 발령번호는 원문 그대로 — 형식(「2024-35」인지 「제2024-35호」인지)이 실응답 미확인이라 꾸미지 않는다 */}
                        {`시행 ${i.enforcementDate ?? '-'} · 발령번호 ${i.issueNo ?? '-'}`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <section aria-label="고시 본문" className="min-w-0 space-y-4 overflow-y-auto max-h-[60vh]">
              {detailQuery.isLoading ? (
                <div className="py-6 text-sm text-gray-500">본문을 불러오는 중...</div>
              ) : detailQuery.error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
                  본문을 불러오지 못했습니다. {extractApiError(detailQuery.error).message}
                </div>
              ) : detail ? (
                <>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{detail.rule.name}</h3>
                    <p className="text-xs text-gray-600">
                      {[
                        detail.rule.ministry,
                        detail.rule.ruleKind,
                        detail.rule.issueNo ? `발령번호 ${detail.rule.issueNo}` : null,
                        detail.rule.issueDate ? `발령 ${detail.rule.issueDate}` : null,
                        detail.rule.enforcementDate ? `시행 ${detail.rule.enforcementDate}` : null,
                        detail.rule.amendmentType,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">{`조문 ${detail.articles.length}건`}</h4>
                    {detail.articles.length === 0 ? (
                      <p className="text-sm text-gray-500">조문이 없습니다.</p>
                    ) : (
                      <pre className="p-3 bg-gray-50 rounded-card text-xs whitespace-pre-wrap">
                        {detail.articles.map((a) => a.content).join('\n\n')}
                      </pre>
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">{`별표·서식 ${detail.annexes.length}건`}</h4>
                    {detail.annexes.length === 0 ? (
                      <p className="text-sm text-gray-500">별표가 없습니다.</p>
                    ) : (
                      detail.annexes.map((x) => (
                        <div key={x.id} className="mb-3">
                          <p className="text-xs font-medium text-gray-800">
                            {`${formatAnnexLabel(x.annexKind, x.annexNumber, x.annexSubNumber)} — ${x.title}`}
                          </p>
                          {/* 괘선 표 — 줄바꿈·칸 공백이 구조라 whitespace-pre + 고정폭(법령 별표 모달과 같다) */}
                          <pre className="p-3 bg-gray-50 rounded-card text-xs font-mono whitespace-pre overflow-auto">
                            {x.content}
                          </pre>
                        </div>
                      ))
                    )}
                  </div>

                  {detail.rule.addenda && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">부칙</h4>
                      <pre className="p-3 bg-gray-50 rounded-card text-xs whitespace-pre-wrap">{detail.rule.addenda}</pre>
                    </div>
                  )}
                </>
              ) : null}
            </section>
          </div>
        )}
      </div>
    </Modal>
  );
}
